// src/resources/reports.js
import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    ChipField,
    ReferenceField,
    EditButton,
    Filter,
    SelectInput,
    Edit,
    SimpleForm,
    useRecordContext,
    Toolbar,
    SaveButton,
    Button,
    TextInput,
    FunctionField // <--- تم إضافته هنا لحل المشكلة
} from 'react-admin';
import { 
    Box, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    Divider, 
    Avatar 
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import FlagIcon from '@mui/icons-material/Flag';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { Link } from 'react-router-dom';
import { BanUserButton } from './users';

// --- الفلاتر (كما هي) ---
const ReportFilters = (props) => (
    <Filter {...props}>
        <SelectInput 
            source="status" 
            choices={[
                { id: 'PENDING', name: 'Pending' },
                { id: 'RESOLVED', name: 'Resolved' },
                { id: 'DISMISSED', name: 'Dismissed' },
            ]} 
            alwaysOn 
        />
    </Filter>
);

// --- القائمة (List) ---
export const ReportList = (props) => (
    <List {...props} filters={<ReportFilters />} title="User Reports" sort={{ field: 'createdAt', order: 'DESC' }}>
        <Datagrid rowClick="edit">
            <DateField source="createdAt" label="Date" showTime />
            <ChipField source="reason" />
            <ReferenceField label="Reporter" source="reporterId" reference="users" link="show">
                <TextField source="name" />
            </ReferenceField>
            {/* تمييز المبلَغ عنه بلون مختلف */}
            <ReferenceField label="Reported User" source="reportedUserId" reference="users" link="show">
                <TextField source="name" style={{ color: 'red', fontWeight: 'bold' }} />
            </ReferenceField>
            <ChipField source="status" />
            <EditButton />
        </Datagrid>
    </List>
);

// --- زر الذهاب للشات ---
const ViewChatButton = () => {
    const record = useRecordContext();
    if (!record || !record.ride || !record.ride.chat) return null;

    return (
        <Button
            component={Link}
            to={`/chats/${record.ride.chat.id}/show`}
            label="Open Chat Logs"
            startIcon={<ChatIcon />}
            variant="outlined"
            size="small"
            sx={{ mt: 1, width: '100%' }}
        />
    );
};

// --- شريط الأدوات (حفظ + حظر) ---
const ReportEditToolbar = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    return (
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <SaveButton />
            <Box>
                {/* زر حظر المستخدم المشتبه به */}
                <Typography variant="caption" sx={{ mr: 1 }}>Action against reported user:</Typography>
                <BanUserButton userId={record.reportedUserId} />
            </Box>
        </Toolbar>
    );
};

// --- مكون مساعد لتنسيق العناوين ---
const SectionHeader = ({ icon, title, color = "primary" }) => (
    <Box display="flex" alignItems="center" mb={2}>
        {React.cloneElement(icon, { color: color, sx: { mr: 1 } })}
        <Typography variant="h6">{title}</Typography>
    </Box>
);

// --- مكون مساعد لعرض المستخدم ---
const UserInfoCard = ({ label, source, roleColor }) => (
    <Box 
        sx={{ 
            p: 2, 
            border: '1px solid', 
            borderColor: 'grey.300', 
            borderRadius: 2,
            backgroundColor: roleColor,
            height: '100%'
        }}
    >
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {label}
        </Typography>
        <ReferenceField source={source} reference="users" link="show">
            <Box display="flex" alignItems="center">
                <Avatar sx={{ width: 30, height: 30, mr: 1, bgcolor: 'grey.400' }}>
                    <PersonIcon />
                </Avatar>
                <Box>
                    <TextField source="name" style={{ fontWeight: 'bold', display: 'block' }} />
                    <TextField source="phone" style={{ fontSize: '0.8rem', color: '#666' }} />
                </Box>
            </Box>
        </ReferenceField>
    </Box>
);

// --- صفحة التعديل/المراجعة (Edit) - التصميم المصحح ---
export const ReportEdit = (props) => (
    <Edit {...props} title="Review Report">
        <SimpleForm toolbar={<ReportEditToolbar />} sx={{ maxWidth: '100%' }}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
                
                {/* العمود الأيسر: تفاصيل البلاغ والأطراف */}
                <Grid item xs={12} md={8}>
                    
                    {/* 1. بطاقة أطراف النزاع */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <SectionHeader icon={<PersonIcon />} title="Involved Parties" />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    {/* المبلِغ */}
                                    <UserInfoCard label="Reporter (Victim?)" source="reporterId" roleColor="#f0f7ff" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    {/* المبلَغ عنه */}
                                    <UserInfoCard label="Reported User (Suspect)" source="reportedUserId" roleColor="#fff0f0" />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* 2. بطاقة تفاصيل الشكوى */}
                    <Card>
                        <CardContent>
                            <SectionHeader icon={<FlagIcon />} title="Report Content" color="error" />
                            
                            <Box mb={2}>
                                <Typography variant="caption" color="textSecondary">Reason Selected</Typography>
                                <TextInput source="reason" fullWidth disabled variant="filled" />
                            </Box>

                            <Box mb={2}>
                                <Typography variant="caption" color="textSecondary">User Comment / Description</Typography>
                                <TextInput 
                                    source="comment" 
                                    multiline 
                                    fullWidth 
                                    minRows={4} 
                                    disabled 
                                    variant="outlined" 
                                    sx={{ 
                                        '& .MuiInputBase-input': { 
                                            fontSize: '1.1rem', 
                                            color: '#333',
                                            WebkitTextFillColor: '#333 !important' // Force color even if disabled
                                        } 
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* العمود الأيمن: السياق والإجراءات */}
                <Grid item xs={12} md={4}>
                    
                    {/* 3. بطاقة اتخاذ القرار (الحالة) */}
                    <Card sx={{ mb: 2, borderLeft: '5px solid orange' }}>
                        <CardContent>
                            <SectionHeader icon={<WarningIcon />} title="Resolution Status" color="warning" />
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Update the status after reviewing the evidence.
                            </Typography>
                            <SelectInput 
                                source="status" 
                                choices={[
                                    { id: 'PENDING', name: '⏳ Pending' },
                                    { id: 'RESOLVED', name: '✅ Resolved (Action Taken)' },
                                    { id: 'DISMISSED', name: '❌ Dismissed (False Report)' },
                                ]} 
                                fullWidth
                                variant="outlined"
                            />
                        </CardContent>
                    </Card>

                    {/* 4. بطاقة سياق الرحلة والشات */}
                    <Card>
                        <CardContent>
                            <SectionHeader icon={<DirectionsCarIcon />} title="Ride Context" color="action" />
                            
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                <Typography variant="caption" display="block" color="textSecondary">Related Ride:</Typography>
                                <ReferenceField source="rideId" reference="rides" link="show">
                                    {/* FunctionField لعرض المسار */}
                                    <FunctionField render={record => 
                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                            {/* نتأكد من وجود البيانات قبل العرض */}
                                            {record ? `${record.fromCity || '?'} ➝ ${record.toCity || '?'}` : 'Ride Deleted'}
                                        </Typography>
                                    } />
                                </ReferenceField>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                <Typography variant="caption" display="block" color="textSecondary">Evidence:</Typography>
                                {/* زر الشات الجديد */}
                                <ReferenceField source="rideId" reference="rides" link={false}>
                                    <ViewChatButton />
                                </ReferenceField>
                            </Box>
                        </CardContent>
                    </Card>

                </Grid>
            </Grid>
        </SimpleForm>
    </Edit>
);